/**
 * @file ReportSubmitModal.tsx
 *
 * @description This component provides a dialog modal for police officers to confirm report submission or RMS commit actions.
 * The modal implements a verification step requiring officers to type their full name as a digital signature before proceeding.
 *
 * @module Modals
 * @requires React
 * @requires Material-UI
 */
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext } from "react";
import { BodycamContext } from "../context/BodycamContext";
import { isValidCaseNumber } from "../features/tn-case-number-input/featureFlag";

/**
 * Props interface for the ReportSubmitModal component
 * @interface ReportSubmitModalProps
 * @property {(caseNumber?: string) => void} handleSubmitReport - Callback function triggered when the report is successfully submitted
 * @property {boolean} open - Controls whether the modal is displayed or hidden
 * @property {() => void} handleClose - Callback function triggered when the modal is closed
 * @property {boolean} [isRMSCommit=false] - Flag indicating if this is a Records Management System commit action
 * @property {boolean} [requireCaseNumber=false] - When true, the officer must also enter a case number in YY-123456 format
 */
interface ReportSubmitModalProps {
  handleSubmitReport: (caseNumber?: string) => void;
  open: boolean;
  handleClose: () => void;
  isRMSCommit?: boolean;
  requireCaseNumber?: boolean;
}

/**
 * @name ReportSubmitModal
 *
 * @description A modal dialog component for confirming report submission or RMS commit actions
 *
 * This component displays a confirmation dialog requiring officers to enter their full name
 * as a digital signature before proceeding with report submission or RMS commit.
 * Different confirmation messages are shown based on whether this is a standard report
 * signature or an RMS submission.
 *
 * @param {ReportSubmitModalProps} props - Component props
 *
 * @returns {React.ReactElement} The rendered modal component
 */
export const ReportSubmitModal: React.FC<ReportSubmitModalProps> = ({
  handleSubmitReport,
  open,
  handleClose,
  isRMSCommit = false,
  requireCaseNumber = false,
}) => {
  // State to track the name entered by the officer
  const [enteredName, setEnteredName] = React.useState("");

  // State to track whether the entered name is valid (matches the officer's name)
  const [nameValid, setNameValid] = React.useState(false);

  // Case number input (only used when requireCaseNumber is true)
  const [caseNumber, setCaseNumber] = React.useState("");
  const [caseNumberTouched, setCaseNumberTouched] = React.useState(false);

  // Reset case number state when the modal closes so stale input doesn't persist on reopen.
  React.useEffect(() => {
    if (!open) {
      setCaseNumber("");
      setCaseNumberTouched(false);
    }
  }, [open]);

  // Access user information from context
  const { userInfo } = useContext(BodycamContext);
  const [userProfile] = userInfo;

  // Construct full name from user profile
  const userName = userProfile?.firstName + " " + userProfile?.lastName;

  // Validates if the entered name matches the officer's full name
  const validateApproval = (value: string) => {
    setEnteredName(value);
    if (value === userName) {
      setNameValid(true);
    } else {
      setNameValid(false);
    }
  };

  const caseNumberValid = isValidCaseNumber(caseNumber);
  const showCaseNumberError =
    requireCaseNumber && caseNumberTouched && !caseNumberValid;
  const submitDisabled =
    !nameValid || (requireCaseNumber && !caseNumberValid);

  // Strip non-digits, cap at 8 digits (YY + 6), and reinsert the hyphen after YY.
  const formatCaseNumberInput = (raw: string): string => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };

  const handleConfirmClick = () => {
    if (submitDisabled) return;
    handleSubmitReport(requireCaseNumber ? caseNumber : undefined);
  };

  const confirmButtonText = isRMSCommit ? "Submit to RMS" : "Sign Report";

  const confirmationMessage = isRMSCommit
    ? `Are you sure you want to submit the report to RMS?
        This action cannot be undone.
          Once submitted, document will be signed and no other changes can be
          made to the report. The report will also be sent to your RMS to be imported and you will receive an event number
          corresponding to the RMS incident created.`
    : `Are you sure you want to sign the report? This action cannot be undone.
          Once approved, document will be signed and no other changes can be
          made to the report.`;

  return (
    <Dialog
      open={open}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        marginTop: "15px",
      }}
    >
      <DialogContent
        sx={{
          backgroundColor: "background.paper",
          padding: "20px",
          borderRadius: "5px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          textAlign: "center",
        }}
      >
        {/* Modal title */}
        <Typography variant="h3" fontSize="25px" margin="10px">
          Document Approval Confirmation
        </Typography>

        {/* Confirmation message explaining the action */}
        <Typography fontSize="15px" margin="10px">
          {confirmationMessage}
        </Typography>

        {/* Instructions for name entry */}
        <Typography fontSize="15px" margin="10px">
          To proceed, please type out your name to confirm that you have
          reviewed the document. This will serve as your digital signature.
        </Typography>

        {/* Alternative action instruction */}
        <Typography fontSize="15px" margin="10px">
          Otherwise, click cancel to return to the pending reports page.
        </Typography>

        {requireCaseNumber && (
          <TextField
            type="text"
            id="caseNumber"
            name="caseNumber"
            label="Case Number"
            value={caseNumber}
            sx={{
              width: "100%",
              margin: "10px",
              outline: "none",
              transition: "border-color 0.3s ease, box-shadow 0.3s ease",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
            }}
            placeholder="YY-123456"
            required
            error={showCaseNumberError}
            helperText={
              showCaseNumberError
                ? "Case number must be 8 digits in YY-123456 format (e.g. 26-000123)."
                : "Enter the 8-digit case number for the incident (the hyphen is added automatically)."
            }
            inputProps={{
              inputMode: "numeric",
              autoComplete: "off",
              maxLength: 9,
            }}
            onChange={(e) => setCaseNumber(formatCaseNumberInput(e.target.value))}
            onBlur={() => setCaseNumberTouched(true)}
          />
        )}

        {/* Text field for officer name entry */}
        <TextField
          type="text"
          id="officerName"
          name="officerName"
          value={enteredName}
          sx={{
            width: "100%",
            margin: "10px",
            // border: "2px solid #d1d5db" /* Light gray border */,
            // borderRadius: "6px",
            outline: "none",
            transition: "border-color 0.3s ease, box-shadow 0.3s ease",
            // backgroundColor: "#f9fafb" /* Light background color */,
            // color: "#111827" /* Dark text color */,
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
          }}
          placeholder={userName}
          required
          onChange={(e) => validateApproval(e.target.value)}
        />

        {/* Cancel button */}
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{ margin: "5px", width: "200px" }}
          color="error"
        >
          <Typography fontSize="15px">Cancel</Typography>
        </Button>

        {/* Submit/Confirm button - disabled until name (and case number, if required) validation passes */}
        <Button
          onClick={handleConfirmClick}
          disabled={submitDisabled}
          variant="contained"
          sx={{ margin: "5px", width: "200px" }}
        >
          <Typography fontSize="15px">{confirmButtonText}</Typography>
        </Button>
      </DialogContent>
    </Dialog>
  );
};
